import { FC, Fragment, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EyeButton from 'components/EyeButton/EyeButton';
import FileViewModal from 'components/FileViewModal/FileViewModal';
import TierPermissionsWrapper from 'components/TierPermissionsWrapper/TierPermissionsWrapper';

import { GTMEventLogger } from 'utils/analytics';
import { getLatestAttachmentOfType } from 'utils/getLatestAttachmentOfType';

import { AttachmentTypes } from 'types/api';
import { Attachment } from 'types/commons';

import {
  ClientTierFeatures,
  ClientTiers,
  GTMEvents,
  JobseekerAttachmentTypes
} from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import { TranslationKey } from '../../types/i18next';

const FEATURES_TITLES: Partial<Record<ClientTierFeatures, TranslationKey>> = {
  [ClientTierFeatures.Resumes]: 'attachment.resume'
};

interface EyeAttachmentPreviewProps {
  // we can add more attachment types when needed
  attachments: Attachment<JobseekerAttachmentTypes>[];
  // type to get the last attachment to preview
  attachmentType: AttachmentTypes;
  minimalTier?: ClientTiers;
  feature?: ClientTierFeatures;
  event?: GTMEvents;
}

const EyeAttachmentPreview: FC<EyeAttachmentPreviewProps> = ({
  attachments,
  attachmentType,
  minimalTier,
  feature,
  event
}: EyeAttachmentPreviewProps) => {
  const { t } = useTranslation();
  const [isShowPreviewModal, setIsShowPreviewModal] = useState<boolean>(false);

  const attachment = getLatestAttachmentOfType(attachments, attachmentType);

  const handleOpenPreviewModal = (e: MouseEvent) => {
    e.stopPropagation();
    setIsShowPreviewModal(true);

    if (event) {
      GTMEventLogger(event);
    }
  };

  const handleClosePreviewModal = () => {
    setIsShowPreviewModal(false);
  };

  const WrapperComponent = minimalTier ? TierPermissionsWrapper : Fragment;

  const featureTitle =
    feature && FEATURES_TITLES[feature]
      ? t(FEATURES_TITLES[feature] as TranslationKey)
      : undefined;

  const title = featureTitle
    ? t('attachment.featurePreview', { feature: featureTitle })
    : t('attachment.preview');

  return (
    <WrapperComponent
      minimalTier={minimalTier as ClientTiers}
      isDisplayWhenUnavailable
      feature={feature}
    >
      {attachment ? (
        <EyeButton onClick={handleOpenPreviewModal} />
      ) : (
        FALLBACK_VALUE
      )}

      {isShowPreviewModal && (
        <FileViewModal
          attachment={attachment}
          onClose={handleClosePreviewModal}
          title={title}
          isOpen
        />
      )}
    </WrapperComponent>
  );
};

export default EyeAttachmentPreview;
